package dev.danko.roblox_craft_ai.uikit.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.runtime.remember
import dev.danko.roblox_craft_ai.shared.model.RobloxMeshLimits
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Input
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import dev.danko.roblox_craft_ai.shared.model.GenerationQuality
import dev.danko.roblox_craft_ai.uikit.i18n.strings
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import kotlinx.browser.localStorage
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button

/** The user's last quality choice for sketches and 3D models, remembered in this browser. */
object QualityPrefs {
    private const val SKETCH_KEY = "rc.quality.sketch"
    private const val MESH_KEY = "rc.quality.mesh"
    private const val VARIANTS_KEY = "rc.quality.variants"

    // The accurate sketch costs a few credits more but decides most of the 3D result, so it is the default;
    // the expensive 3D levels are only used when picked.
    var sketch by mutableStateOf(read(SKETCH_KEY) ?: GenerationQuality.HIGH)
        private set
    var mesh by mutableStateOf(read(MESH_KEY) ?: GenerationQuality.STANDARD)
        private set
    /** Sketches drawn at once: 1 or 4 to choose from. */
    var variants by mutableStateOf(readInt(VARIANTS_KEY)?.takeIf { it == 1 || it == 4 } ?: 4)
        private set

    fun chooseVariants(value: Int) {
        variants = if (value >= 4) 4 else 1
        try { localStorage.setItem(VARIANTS_KEY, variants.toString()) } catch (_: Throwable) {}
    }

    private fun readInt(key: String): Int? = try { localStorage.getItem(key)?.toIntOrNull() } catch (_: Throwable) { null }

    fun chooseSketch(value: GenerationQuality) {
        sketch = if (value == GenerationQuality.MAX) GenerationQuality.HIGH else value
        write(SKETCH_KEY, sketch)
    }

    fun chooseMesh(value: GenerationQuality) {
        mesh = value
        write(MESH_KEY, value)
    }

    private fun read(key: String): GenerationQuality? = try {
        localStorage.getItem(key)?.let { v -> GenerationQuality.entries.firstOrNull { it.name == v } }
    } catch (_: Throwable) {
        null
    }

    private fun write(key: String, value: GenerationQuality) {
        try { localStorage.setItem(key, value.name) } catch (_: Throwable) {}
    }
}

/** Credit kind names on the server for each choice (see UsageKind). */
object QualityCredits {
    fun sketchKind(quality: GenerationQuality, reroll: Boolean = false) =
        if (quality == GenerationQuality.STANDARD) (if (reroll) "CONCEPT_REROLL" else "CONCEPT") else "CONCEPT_HIGH"

    fun sketchExtraKind(quality: GenerationQuality) = if (quality == GenerationQuality.STANDARD) "CONCEPT_EXTRA" else "CONCEPT_HIGH_EXTRA"

    /** Credits of one drawing: the first sketch plus each extra variant; null when prices are unknown. */
    fun sketchTotal(costs: Map<String, Int>?, quality: GenerationQuality, variants: Int, reroll: Boolean = false): Int? {
        val first = costs?.get(sketchKind(quality, reroll)) ?: return null
        val extra = costs[sketchExtraKind(quality)] ?: 0
        return first + extra * (variants.coerceIn(1, 4) - 1)
    }

    fun meshKind(quality: GenerationQuality) = when (quality) {
        GenerationQuality.STANDARD -> "MESH_3D"
        GenerationQuality.HIGH -> "MESH_3D_HIGH"
        GenerationQuality.MAX -> "MESH_3D_MAX"
    }
}

class QualityOption<T>(val value: T, val label: String, val hint: String, val cost: String?)

fun sketchOptions(costOf: (String) -> String?): List<QualityOption<GenerationQuality>> {
    val q = dev.danko.roblox_craft_ai.uikit.i18n.LocaleController.strings.quality
    return listOf(
        QualityOption(GenerationQuality.STANDARD, q.sketchStandard, q.sketchStandardHint, costOf(QualityCredits.sketchKind(GenerationQuality.STANDARD))),
        QualityOption(GenerationQuality.HIGH, q.sketchHigh, q.sketchHighHint, costOf(QualityCredits.sketchKind(GenerationQuality.HIGH)))
    )
}

fun meshOptions(costOf: (String) -> String?): List<QualityOption<GenerationQuality>> {
    val q = dev.danko.roblox_craft_ai.uikit.i18n.LocaleController.strings.quality
    return listOf(
        QualityOption(GenerationQuality.STANDARD, q.meshStandard, q.meshStandardHint, costOf(QualityCredits.meshKind(GenerationQuality.STANDARD))),
        QualityOption(GenerationQuality.HIGH, q.meshHigh, q.meshHighHint, costOf(QualityCredits.meshKind(GenerationQuality.HIGH))),
        QualityOption(GenerationQuality.MAX, q.meshMax, q.meshMaxHint, costOf(QualityCredits.meshKind(GenerationQuality.MAX)))
    )
}

/** A labelled row of choices; each shows its name, credit price and a short hint (also as a tooltip). */
@Composable
fun <T> QualityPicker(title: String, options: List<QualityOption<T>>, selected: T, enabled: Boolean = true, onSelect: (T) -> Unit) {
    val palette = LocalAppTheme.current.colors
    Column(Modifier.fillMaxWidth().gap(6.px)) {
        SpanText(title, Modifier.fontSize(12.px).fontWeight(FontWeight.SemiBold).color(palette.textSecondary))
        Row(Modifier.fillMaxWidth().gap(6.px).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically) {
            options.forEach { option ->
                val isSelected = option.value == selected
                Button(
                    Modifier
                        .padding(topBottom = 6.px, leftRight = 12.px)
                        .borderRadius(10.px)
                        .border(1.px, LineStyle.Solid, if (isSelected) palette.accent else palette.borderStrong)
                        .backgroundColor(if (isSelected) palette.accent.toRgb().copy(alpha = 40) else Colors.Transparent)
                        .color(palette.textPrimary)
                        .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
                        .opacity(if (enabled) 1.0 else 0.6)
                        .textAlign(com.varabyte.kobweb.compose.css.TextAlign.Left)
                        .styleModifier { property("font-family", "inherit") }
                        .onClick { if (enabled) onSelect(option.value) }
                        .toAttrs {
                            attr("type", "button")
                            attr("aria-pressed", isSelected.toString())
                            attr("title", option.hint)
                            if (!enabled) attr("disabled", "")
                        }
                ) {
                    Column(Modifier.gap(1.px)) {
                        SpanText(
                            listOfNotNull(option.label, option.cost).joinToString(" · "),
                            Modifier.fontSize(13.px).fontWeight(FontWeight.SemiBold)
                        )
                        SpanText(option.hint, Modifier.fontSize(11.px).color(palette.textSecondary))
                    }
                }
            }
        }
    }
}

/** Sketch and 3D choices together, as shown in the prompt box for 3D models. */
@Composable
fun MeshQualityChoices(costOf: (String) -> String?, enabled: Boolean = true, showSketch: Boolean = true) {
    val q = strings().quality
    val palette = LocalAppTheme.current.colors
    Column(Modifier.fillMaxWidth().gap(10.px)) {
        if (showSketch) {
            QualityPicker(q.sketchTitle, sketchOptions(costOf), QualityPrefs.sketch, enabled) { QualityPrefs.chooseSketch(it) }
            // The choice reuses QualityPicker's look; STANDARD stands for one sketch, HIGH for four.
            val extra = costOf(QualityCredits.sketchExtraKind(QualityPrefs.sketch))
            QualityPicker(
                q.variantsTitle,
                listOf(
                    QualityOption(GenerationQuality.STANDARD, q.variantsOne, q.variantsOneHint, null),
                    QualityOption(GenerationQuality.HIGH, q.variantsFour, q.variantsFourHint, extra?.let { "+3 × $it" })
                ),
                if (QualityPrefs.variants >= 4) GenerationQuality.HIGH else GenerationQuality.STANDARD,
                enabled
            ) { QualityPrefs.chooseVariants(if (it == GenerationQuality.STANDARD) 1 else 4) }
        }
        QualityPicker(q.meshTitle, meshOptions(costOf), QualityPrefs.mesh, enabled) { QualityPrefs.chooseMesh(it) }
        TrianglePicker(enabled)
        TexturePicker(enabled)
        SpanText(q.note, Modifier.fontSize(12.px).color(palette.textSecondary))
    }
}

/** Triangle target for the next 3D model, remembered in this browser; [RobloxMeshLimits.KEEP_DETAIL] = high-poly. */
object TrianglePrefs {
    private const val KEY = "rc.mesh.triangles"
    const val MOB = 5_000
    const val CHARACTER = 12_000
    const val BOSS = RobloxMeshLimits.MAX_TRIANGLES
    val PRESETS = listOf(MOB, CHARACTER, BOSS, RobloxMeshLimits.KEEP_DETAIL)

    var target by mutableStateOf(
        try { localStorage.getItem(KEY)?.toIntOrNull() } catch (_: Throwable) { null }?.takeIf(RobloxMeshLimits::isValidTarget)
            ?: RobloxMeshLimits.DEFAULT_TRIANGLES
    )
        private set

    fun choose(value: Int) {
        target = if (value == RobloxMeshLimits.KEEP_DETAIL) value
            else value.coerceIn(RobloxMeshLimits.MIN_TRIANGLES, RobloxMeshLimits.MAX_TRIANGLES)
        try { localStorage.setItem(KEY, target.toString()) } catch (_: Throwable) {}
    }
}

private const val CUSTOM_TRIANGLES = -1

/** Presets for typical Roblox uses, high-poly, or an own number between 500 and 20 000. */
@Composable
fun TrianglePicker(enabled: Boolean = true) {
    val q = strings().quality
    val palette = LocalAppTheme.current.colors
    var custom by remember { mutableStateOf(TrianglePrefs.target !in TrianglePrefs.PRESETS) }
    val options = listOf(
        QualityOption(TrianglePrefs.MOB, q.trianglesMob, q.trianglesUpTo(TrianglePrefs.MOB), null),
        QualityOption(TrianglePrefs.CHARACTER, q.trianglesCharacter, q.trianglesUpTo(TrianglePrefs.CHARACTER), null),
        QualityOption(TrianglePrefs.BOSS, q.trianglesBoss, q.trianglesUpTo(TrianglePrefs.BOSS), null),
        QualityOption(RobloxMeshLimits.KEEP_DETAIL, q.trianglesHighPoly, q.trianglesHighPolyHint, null),
        QualityOption(CUSTOM_TRIANGLES, q.trianglesCustom, q.trianglesUpTo(if (custom) TrianglePrefs.target else RobloxMeshLimits.MAX_TRIANGLES), null)
    )
    Column(Modifier.fillMaxWidth().gap(6.px)) {
        QualityPicker(q.trianglesTitle, options, if (custom) CUSTOM_TRIANGLES else TrianglePrefs.target, enabled) { value ->
            custom = value == CUSTOM_TRIANGLES
            if (!custom) TrianglePrefs.choose(value)
            else if (TrianglePrefs.target == RobloxMeshLimits.KEEP_DETAIL) TrianglePrefs.choose(RobloxMeshLimits.DEFAULT_TRIANGLES)
        }
        if (custom) {
            Input(
                type = InputType.Number,
                attrs = Modifier
                    .width(140.px)
                    .padding(topBottom = 6.px, leftRight = 10.px)
                    .borderRadius(10.px)
                    .border(1.px, LineStyle.Solid, palette.borderStrong)
                    .backgroundColor(palette.surface)
                    .color(palette.textPrimary)
                    .toAttrs {
                        attr("min", RobloxMeshLimits.MIN_TRIANGLES.toString())
                        attr("max", RobloxMeshLimits.MAX_TRIANGLES.toString())
                        attr("step", "500")
                        attr("aria-label", q.trianglesTitle)
                        if (!enabled) attr("disabled", "")
                        value(TrianglePrefs.target)
                        // Clamped when the field is left, so typing "1" on the way to "15000" is not cut to 500.
                        onChange { event -> event.value?.toInt()?.let(TrianglePrefs::choose) }
                    }
            )
        }
        SpanText(q.trianglesNote, Modifier.fontSize(12.px).color(palette.textSecondary))
    }
}

/** Texture size the generator paints for the next 3D model, remembered in this browser. */
object TexturePrefs {
    private const val KEY = "rc.mesh.texture"
    val SIZES = listOf(1024, 2048, 4096)

    var size by mutableStateOf(try { localStorage.getItem(KEY)?.toIntOrNull() } catch (_: Throwable) { null }?.takeIf { it in SIZES } ?: 1024)
        private set

    fun choose(value: Int) {
        size = value.takeIf { it in SIZES } ?: 1024
        try { localStorage.setItem(KEY, size.toString()) } catch (_: Throwable) {}
    }
}

@Composable
fun TexturePicker(enabled: Boolean = true) {
    val q = strings().quality
    val palette = LocalAppTheme.current.colors
    Column(Modifier.fillMaxWidth().gap(6.px)) {
        QualityPicker(
            q.textureTitle,
            listOf(
                QualityOption(1024, "1K", q.texture1kHint, null),
                QualityOption(2048, "2K", q.texture2kHint, null),
                QualityOption(4096, "4K", q.texture4kHint, null)
            ),
            TexturePrefs.size,
            enabled
        ) { TexturePrefs.choose(it) }
        SpanText(q.textureNote, Modifier.fontSize(12.px).color(palette.textSecondary))
    }
}
